/**
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	HttpResponse
} from '@angular/common/http';
import {
	Component,
	Input,
	OnInit
} from '@angular/core';
import {
	Router
} from '@angular/router';
import {
	EntityInstanceApiService
} from '@api/services/entities/entity-instance.api.service';
import {
	EntityTypeApiService
} from '@api/services/entities/entity-type.api.service';
import {
	EntityInstanceComponent
} from '@entity/components/entity-instance/entity-instance.component';
import {
	EntityService
} from '@entity/services/entity.service';
import {
	InsuranceConstants
} from '@insurance/constants/insurance-constants';
import {
	ICommissionDisbursementSummary
} from '@insurance/interfaces/commission-disbursement-summary.interface';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	CommissionDisbursementStatus
} from '@shared/constants/enums/commission-disbursment-status.enum';
import {
	SecurityRightCategory
} from '@shared/constants/enums/security-right-category.enum';
import {
	SecurityRightType
} from '@shared/constants/enums/security-right-type.enum';
import {
	CommonListDirective
} from '@shared/directives/common-list-directive';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	ApiFilterHelper
} from '@shared/helpers/api-filter.helper';
import {
	ApiHelper
} from '@shared/helpers/api.helper';
import {
	DateHelper
} from '@shared/helpers/date.helper';
import {
	ObjectHelper
} from '@shared/helpers/object.helper';
import {
	SecurityHelper
} from '@shared/helpers/security.helper';
import {
	StringHelper
} from '@shared/helpers/string.helper';
import {
	IDynamicComponent
} from '@shared/interfaces/application-objects/dynamic-component.interface';
import {
	ICommonListContext
} from '@shared/interfaces/dynamic-interfaces/common-list-context.interface';
import {
	ICommonListFilter
} from '@shared/interfaces/dynamic-interfaces/common-list-filter.interface';
import {
	ICommonListItem
} from '@shared/interfaces/dynamic-interfaces/common-list-item.interface';
import {
	ICommonListSort
} from '@shared/interfaces/dynamic-interfaces/common-list-sort.interface';
import {
	IEntityInstance
} from '@shared/interfaces/entities/entity-instance.interface';
import {
	IEntityType
} from '@shared/interfaces/entities/entity-type.interface';
import {
	ISecureMenuItem
} from '@shared/interfaces/secure-menu-item.interface';
import {
	ISecurityEntityTypeDefinition
} from '@shared/interfaces/security/security-entity-type-definition.interface';
import {
	IActionResponse
} from '@shared/interfaces/workflow/action-response.interface';
import {
	SessionService
} from '@shared/services/session.service';
import {
	DateTime
} from 'luxon';

@Component({
	selector: 'app-commissions-list',
	templateUrl: './commissions-list.component.html',
	styleUrls: [
		'./commissions-list.component.scss'
	]
})

/**
 * A component representing commission disbursement item list view.
 *
 * @export
 * @class CommissionsListComponent
 * @extends {CommonListDirective}
 * @implements {IDynamicComponent<Component, any>}
 */
export class CommissionsListComponent
	extends CommonListDirective<
		ICommissionDisbursementSummary,
		ICommissionDisbursementSummary>
	implements OnInit, IDynamicComponent<Component, any>
{
	/**
	 * Initializes a new instance of the commission list component.
	 *
	 * @param {EntityService} entityService
	 * The entity service.
	 * @param {EntityTypeApiService} entityTypeApiService
	 * The entity type api service.
	 * @param {EntityInstanceApiService} entityInstanceApiService
	 * The entity instance api service.
	 * @param {SessionService} sessionService
	 * The session service.
	 * @param {Router} router
	 * The router for navigation.

	 * @memberof CommissionsListComponent
	 */
	public constructor(
		public entityservice: EntityService,
		public entityTypeApiService: EntityTypeApiService,
		public entityInstanceApiService: EntityInstanceApiService,
		public sessionService: SessionService,
		public router: Router)
	{
		super();
	}

	/**
	 * Gets or sets the wildcard child filter.
	 *
	 * @type {string}
	 * @memberof CommissionsListComponent
	 */
	@Input() public wildcardChildFilter: string = AppConstants.empty;

	/**
	 * Gets or sets the security definitions.
	 *
	 * @type {ISecurityEntityTypeDefinition[]}
	 * @memberof CommissionsListComponent
	 */
	@Input() public securityDefinitions: ISecurityEntityTypeDefinition[] = [];

	/**
	 * Gets or sets the list of sorts available in this list component.
	 *
	 * @type {ICommonListSort[]}
	 * @memberof CommissionsListComponent
	 */
	public sorters: ICommonListSort[] =
		<ICommonListSort[]>
		[
			{
				name: 'Create Date',
				value: AppConstants.empty,
				direction: AppConstants.sortDirections.descending,
				iconAsc: AppConstants.cssClasses.sortAscending,
				iconDesc: AppConstants.cssClasses.sortDescending
			}
		];

	/**
	 * Gets the list of users who created the commission disbursements.
	 *
	 * @type {IEntityInstance[]}
	 * @memberof CommissionsListComponent
	 */
	public createdByList: IEntityInstance[] = [];

	/**
	 * Gets the search flag that indicates that the filter from
	 * the search input.
	 *
	 * @type {string}
	 * @memberof CommissionsListComponent
	 */
	private readonly searchFlag: string = 'Search';

	/**
	 * Performs data operation that need to happen before mapping.
	 *
	 * @type {Promise<void>}
	 * @returns
	 * An empty promise.
	 * @member CommissionsListComponent
	 */
	public async performPreMappingRequirements(): Promise<void>
	{
		await this.setSecurityDefinitions();

		await this.setCreatableEntityTypes(
			InsuranceConstants
				.insuranceEntityTypes
				.commissionDisbursement);

		if (AnyHelper.isNullOrEmptyArray(this.listData))
		{
			return;
		}

		const createdByIds: string[] =
			this.listData.map((item: any) =>
				`${item.createdById}`);

		const createdByFilter: string =
			AppConstants.commonProperties.id
				+ ' in ('
				+ ApiFilterHelper.commaSeparatedStringValues(
					createdByIds,
					AppConstants.empty)
				+ ')';

		this.createdByList =
			await this.getFullCreatorDataSet(createdByFilter);
	}

	/**
	 * Generates a common list context that can be consumed by a common list
	 * displayed in this component.
	 *
	 * @param {ICommonListItem<IEntityInstance>[]} data
	 * The collection of data that will be displayed in this list.
	 * @returns {ICommonListContext<IEntityInstance>}
	 * The common list context to display.
	 * @memberof CommissionsListComponent
	 */
	public generateCommonListContext(
		data: ICommonListItem<ICommissionDisbursementSummary>[]):
		ICommonListContext<ICommissionDisbursementSummary>
	{
		const listFilters: ICommonListFilter[] =
			<ICommonListFilter[]>
			[
				{
					name: CommissionDisbursementStatus.Pending,
					value:
						`status eq "${CommissionDisbursementStatus.Pending}"`
				},
				{
					name: CommissionDisbursementStatus.Approved,
					value:
						`status eq "${CommissionDisbursementStatus.Approved}"`
				},
				{
					name: CommissionDisbursementStatus.Rejected,
					value:
						`status eq "${CommissionDisbursementStatus.Rejected}"`
				}
			];

		const generatedCommonListContext:
			ICommonListContext<ICommissionDisbursementSummary> =
				{
					data: data,
					supportMarkdown: true,
					searchable: true,
					sortable: true,
					sorters: this.sorters,
					searchFilterFormat: this.searchFlag,
					actions: this.creatableEntityTypes?.length > 0
						? [
							{
								icon: 'fa fa-plus-circle',
								command: async (event: any) =>
								{
									await this.navigateToWizard();
									event.stopPropagation();
								},
								securityRightCategory:
									SecurityRightCategory.Action,
								securityRightPath:
									AppConstants.apiMethods.create,
								securityRightType:
									SecurityRightType.execute
							}
						]
						: [],
					filters: listFilters,
					onFilterChange: (source, filters) =>
						this.filterChanged(source, filters),
					onSortChange: (source, sorter) =>
						this.handleSortingChange(source, sorter)
				};

		return generatedCommonListContext;
	}

	/**
	 * Navigates to the create wizard.
	 *
	 * @type {Promise<void>}
	 * @returns
	 * An empty promise.
	 * @member CommissionsListComponent
	 */
	public async navigateToWizard(): Promise<void>
	{
		const organization: IEntityInstance =
			(<EntityInstanceComponent>this.context.source).entityInstance;

		const entityType: IEntityType =
			(<EntityInstanceComponent>this.context.source).entityType;

		const activeExists: boolean = await this.activeDisbursementExists();

		this.router.navigate(
			[
				`${AppConstants.moduleNames.organizations}/entities`
					+ '/display/'
					+ 'BasePage.Wizard.InsuranceCompany'
					+ '.CreateCommissionDisbursement/'
					+ AppConstants.viewTypes.create
			],
			{
				skipLocationChange: true,
				queryParams:
				{
					routeData:
						ObjectHelper.mapRouteData(
							{
								data: {
									organizationName:
									organization.data.name.legalName,
									organizationId: organization.id,
									organizationType: entityType.name,
									organizationGroup: entityType.group,
									activeSnapshotExists: activeExists,
									cancelRoute: this.router.url
								}
							})
				}
			});
	}

	/**
	 * Determines if there is an existing disbursement pending.
	 *
	 * @type {Promise<boolean>}
	 * @returns
	 * The result true if there are pending disbursements.
	 * @member CommissionsListComponent
	 */
	public async activeDisbursementExists(): Promise<boolean>
	{
		const organization: IEntityInstance =
			(<EntityInstanceComponent>this.context.source).entityInstance;

		const entityType: IEntityType =
			(<EntityInstanceComponent>this.context.source).entityType;

		this.entityInstanceApiService.entityInstanceTypeGroup =
			entityType.group;

		const activeDisbursements: IEntityInstance[] =
			await this.entityInstanceApiService
				.getChildren(
					organization.id,
					`status eq "${CommissionDisbursementStatus.Pending}"`,
					'Id Desc',
					0,
					1,
					InsuranceConstants
						.insuranceEntityTypeGroups
						.commissionDisbursements);

		return activeDisbursements.length > 0;
	}

	/**
	 * Gets a full data set of creators.
	 *
	 * @type {Promise<IEntityInstance[]>}
	 * @returns
	 * all creators of the disbursments.
	 * @member CommissionsListComponent
	 */
	public async getFullCreatorDataSet(
		createdByFilter: string): Promise<IEntityInstance[]>
	{
		this.entityInstanceApiService.entityInstanceTypeGroup =
			AppConstants.typeGroups.users;

		return await ApiHelper.getFullDataSet(
			this.entityInstanceApiService,
			createdByFilter,
			AppConstants.empty);
	}

	/**
	 * A method to generate an initial filter for all calls to this list.
	 *
	 * @rule
	 * This filter will be used for all calls, including those in the
	 * list context filter set.
	 *
	 * @returns {string}
	 * A filter value used for all data loads in this list.
	 * @memberof CommissionsListComponent
	 */
	public getPrimaryFilter(): string
	{
		return AppConstants.empty;
	}

	/**
	 * A method to generate the proper data promise for loading data.
	 *
	 * @param {string} filter
	 * A value representing the compiled filters from the common list filter
	 * array.
	 * @param {string} orderBy
	 * A value representing the compiled order by from the common list sort.
	 * @returns {Promise<ICommissionDisbursementSummary[]>}
	 * A data promise with an applied filter, order, and offset.
	 * @memberof CommissionsListComponent
	 */
	public async getData(
		filter: string,
		orderBy: string): Promise<ICommissionDisbursementSummary[]>
	{
		const systemId: number =
			Number.parseInt(
				AppConstants.systemId,
				AppConstants.parseRadix);

		this.entityInstanceApiService.entityInstanceTypeGroup =
			AppConstants.typeGroups.systems;

		const httpResponse: HttpResponse<any> =
			await this.entityInstanceApiService
				.executeAction(
					systemId,
					'GetCommissionDisbursementSummaries',
					null,
					`?filter=${filter}&orderBy=${orderBy.trimStart()}`);

		const actionResponse: IActionResponse =
			httpResponse?.body ?? httpResponse;

		const data: ICommissionDisbursementSummary[] =
			<ICommissionDisbursementSummary[]>actionResponse.value;

		return data;
	}

	/**
	 * Maps and returns a list item that represents
	 * a commission disbursement instance.
	 *
	 * @param {ICommissionDisbursementSummary} disbursement
	 * The disbursement to be mapped into a list item.
	 * @returns {ICommonListItem<ICommissionDisbursementSummary>}
	 * The common list item to display that represents this disbursement.
	 * @memberof CommissionsListComponent
	 */
	public mapToListItem(
		disbursement: ICommissionDisbursementSummary):
		ICommonListItem<ICommissionDisbursementSummary>
	{
		const createDate: string =
			DateHelper.formatDate(
				DateTime.fromISO(
					`${disbursement.createDate}`),
				DateHelper.presetFormats.longDateFormat);

		const decisionDate: string =
			DateHelper.formatDate(
				DateTime.fromISO(
					`${disbursement.decisionDate}`),
				DateHelper.presetFormats.longDateFormat);

		const createdBy: IEntityInstance =
			this.createdByList.find(
				item => item.id === disbursement.createdById);

		const decisionBy: IEntityInstance =
			AnyHelper.isNullOrEmpty(disbursement.decisionBy)
				? null
				: this.createdByList.find(
					(user: IEntityInstance) =>
						user.id.toString()
							=== disbursement.decisionBy);

		const createdByName: string =
			StringHelper.toNameString(
				createdBy.data.firstName,
				createdBy.data.lastName);

		const decisionByName: string =
			AnyHelper.isNull(decisionBy)
				? null
				: StringHelper.toNameString(
					decisionBy.data.firstName,
					decisionBy.data.lastName);

		let statusColor: string = AppConstants.empty;
		switch (disbursement.status)
		{

			case CommissionDisbursementStatus.Pending:
				break;

			case CommissionDisbursementStatus.Approved:
				statusColor = AppConstants.cssClasses.textSuccessColor;
				break;

			case CommissionDisbursementStatus.Rejected:
				statusColor = AppConstants.cssClasses.textErrorColor;
				break;
		}

		return {
			item: disbursement,
			descriptionLineFormat:
				`<i class="fa fa-fw fa-trophy ${statusColor} `
					+ 'item-description-icon"></i>'
					+ `**${disbursement.status} Disbursement**<br>`
					+ (disbursement.status ===
							CommissionDisbursementStatus.Pending
						? `Create Date: ${createDate}`
						: `Decision Date: ${decisionDate}`),
			informationLineFormat:
				AnyHelper.isNull(decisionBy)
					? `Created By: ${createdByName}`
					: `Decision By: ${decisionByName}`,
			informationIcons: [],
			actions: SecurityHelper.scrubMenuItems(
				[
					<ISecureMenuItem>
					{
						icon: 'fa fa-pencil',
						command: (event: any) =>
						{
							this.navigateToComissionDisbursementPage(
								disbursement.id);
							event.stopPropagation();
						},
						securityRightCategory: SecurityRightCategory.Action,
						securityRightPath: AppConstants.apiMethods.update
					}
				],
				InsuranceConstants
					.insuranceEntityTypes
					.commissionDisbursement,
				this.securityDefinitions)
		};
	}

	/**
	 * Sets security definitions associated with current parent instance.
	 *
	 * @protected
	 * @memberof CommissionsListComponent
	 */
	protected async setSecurityDefinitions(): Promise<void>
	{
		const entityInstanceComponent: EntityInstanceComponent =
			<EntityInstanceComponent>this.context.source;

		this.securityDefinitions =
			await SecurityHelper
				.getSupportedChildPermissions(
					entityInstanceComponent.id,
					entityInstanceComponent.entityType.group,
					InsuranceConstants
						.insuranceEntityTypes
						.commissionDisbursement,
					entityInstanceComponent.entityDefinition,
					entityInstanceComponent.entityInstanceApiService,
					entityInstanceComponent.entityTypeApiService);
	}

	/**
	 * Navigates to the the commission disbursement page.
	 *
	 * @param {number} id
	 * The id of the commission disbursement.
	 * @memberof CommissionsListComponent
	 */
	private navigateToComissionDisbursementPage(
		id: number)
	{
		const typeGroup: string =
			InsuranceConstants
				.insuranceEntityTypeGroups
				.commissionDisbursements;

		this.router.navigate(
			[
				`${AppConstants.moduleNames.organizations}/entities`
					+ `/${typeGroup}/`
					+ AppConstants.viewTypes.edit
					+ '/' + id
			],
			{
				queryParams: {
					routeData:
						ObjectHelper.mapRouteData(
							{
								layoutType:
									AppConstants.layoutTypes.full
							})
				}
			});
	}

	/**
	 * Handles the filter changed event.
	 *
	 * @param {any} source
	 * The context sourse.
	 * @param { ICommonListFilter[]} filters
	 * the filters.
	 *
	 * @memberof CommissionsListComponent
	 */
	private filterChanged(
		source: any,
		filters: ICommonListFilter[])
	{
		const searchfilters: ICommonListFilter[] =
			filters
				.filter((filter: ICommonListFilter) =>
					filter.value === this.searchFlag);

		for (const filter of searchfilters)
		{
			const searchTerm: string =
				filter.name.toUpperCase();

			const createdByMatch: any =
				this.createdByList
					.find(creator =>
					{
						const textToSearch: string =
							`${creator.data.userName} `
							+ `${creator.data.firstName} `
							+ `${creator.data.lastName} `;

						const matchIndex: number =
							textToSearch
								.toUpperCase()
								.search(searchTerm);

						return matchIndex > -1;
					});

			filter.value =
				AnyHelper.isNullOrEmpty(createdByMatch)
					? 'CreatedById eq 0'
					: `CreatedById eq ${createdByMatch.id}`;
		}

		this.handleFilterChange(
			source,
			filters);
	}
}